import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const LeaveRequestManager = ({ onBack }) => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Form state for creating new leave request
  const [formData, setFormData] = useState({
    employee_number: '',
    leave_type: 'Vacation',
    start_date: '',
    end_date: '',
    is_partial_day: false,
    start_time: '',
    end_time: '',
    reason: '',
    comments: ''
  });

  useEffect(() => {
    fetchLeaveRequests();
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchLeaveRequests();
  }, [selectedStatus]);

  const fetchLeaveRequests = async () => {
    try {
      setLoading(true);
      // Force cache bust by adding timestamp - v2
      let query = supabase.from('leave_requests_summary').select('*');
      
      if (selectedStatus !== 'all') {
        query = query.eq('status', selectedStatus);
      }
      
      const { data, error } = await query.order('id', { ascending: false });
      
      if (error) throw error;
      setLeaveRequests(data || []);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
      setError('Failed to fetch leave requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      // Get employees from employees table with combined name
      const { data, error } = await supabase
        .from('employees')
        .select('employee_number, payroll_fname, payroll_lname')
        .not('payroll_fname', 'is', null)
        .order('payroll_fname');
      
      if (error) throw error;
      
      // Combine first and last names for display
      const employeesWithNames = data.map(emp => ({
        employee_number: emp.employee_number,
        employee_name: `${emp.payroll_fname || ''} ${emp.payroll_lname || ''}`.trim()
      }));
      
      setEmployees(employeesWithNames);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Calculate hours for partial day
      let calculatedHours = null;
      if (formData.is_partial_day && formData.start_time && formData.end_time) {
        const start = new Date(`1970-01-01T${formData.start_time}`);
        const end = new Date(`1970-01-01T${formData.end_time}`);
        calculatedHours = (end - start) / (1000 * 60 * 60); // Convert to hours
        
        if (calculatedHours <= 0) {
          throw new Error('End time must be after start time');
        }
      }

      const requestData = {
        employee_number: formData.employee_number,
        leave_type: formData.leave_type,
        start_date: formData.start_date,
        end_date: formData.end_date,
        is_partial_day: formData.is_partial_day,
        start_time: formData.is_partial_day ? formData.start_time : null,
        end_time: formData.is_partial_day ? formData.end_time : null,
        hours_requested: calculatedHours,
        reason: formData.reason || null,
        comments: formData.comments || null,
        status: 'Submitted' // Admin created requests are automatically submitted
      };

      const { data, error } = await supabase
        .from('leave_requests')
        .insert([requestData])
        .select('*')
        .single();

      if (error) throw error;

      // Send SMS notification to admins
      if (data) {
        try {
          const selectedEmployee = employees.find(emp => emp.employee_number === formData.employee_number);
          
          const notificationPayload = {
            leave_request_no: data.leave_request_no,
            employee_name: selectedEmployee?.employee_name || 'Unknown',
            employee_number: data.employee_number,
            leave_type: data.leave_type,
            start_date: data.start_date,
            end_date: data.end_date,
            total_days: data.total_days,
            is_partial_day: data.is_partial_day,
            start_time: data.start_time,
            end_time: data.end_time,
            reason: data.reason
          };

          // Send notification via worker
          const response = await fetch('https://paystub.roborx.workers.dev/api/notify-leave-request', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(notificationPayload),
          });

          const result = await response.json();
          
          if (result.success) {
            console.log('✅ Leave request notification sent successfully:', result.message);
          } else {
            console.warn('⚠️ Leave request notification failed:', result.message);
          }
        } catch (notificationError) {
          console.error('Failed to send leave request notification:', notificationError);
          // Don't throw - the leave request was created successfully
        }
      }

      // Reset form
      setFormData({
        employee_number: '',
        leave_type: 'Vacation',
        start_date: '',
        end_date: '',
        is_partial_day: false,
        start_time: '',
        end_time: '',
        reason: '',
        comments: ''
      });
      
      setShowCreateModal(false);
      fetchLeaveRequests();
      
    } catch (error) {
      console.error('Error creating leave request:', error);
      setError(error.message || 'Failed to create leave request');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (requestId, newStatus, adminComments = '') => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('leave_requests')
        .update({ 
          status: newStatus,
          admin_comments: adminComments || null
        })
        .eq('id', requestId);

      if (error) throw error;
      
      fetchLeaveRequests();
      setShowDetailModal(false);
      
    } catch (error) {
      console.error('Error updating leave request:', error);
      setError('Failed to update leave request');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return new Date(`1970-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'Drafted': 'bg-gray-100 text-gray-800',
      'Submitted': 'bg-blue-100 text-blue-800',
      'Approved': 'bg-green-100 text-green-800',
      'Rejected': 'bg-red-100 text-red-800',
      'Paid': 'bg-purple-100 text-purple-800',
      'Unpaid': 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getLeaveTypeColor = (type) => {
    const colors = {
      'Vacation': 'bg-blue-50 text-blue-700',
      'Sick Leave': 'bg-red-50 text-red-700',
      'Personal Unpaid': 'bg-orange-50 text-orange-700',
      'Other': 'bg-gray-50 text-gray-700'
    };
    return colors[type] || 'bg-gray-50 text-gray-700';
  };

  if (loading && leaveRequests.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-lg text-gray-600">Loading leave requests...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leave Request Management</h1>
          <p className="text-gray-600">Manage employee leave requests and approvals</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
          >
            Create Request
          </button>
          <button
            onClick={onBack}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
          <button 
            onClick={() => setError('')}
            className="text-red-600 underline text-sm mt-1"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex gap-4 items-center">
          <label className="text-sm font-medium text-gray-700">Filter by Status:</label>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1 text-sm"
          >
            <option value="all">All Statuses</option>
            <option value="Drafted">Drafted</option>
            <option value="Submitted">Submitted</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Paid">Paid</option>
            <option value="Unpaid">Unpaid</option>
          </select>
          <div className="text-sm text-gray-600">
            Showing {leaveRequests.length} request{leaveRequests.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Leave Requests Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Request #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dates
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Days
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submitted
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {leaveRequests.map((request) => (
                <tr key={request.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {request.leave_request_no}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {request.employee_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      #{request.employee_number}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getLeaveTypeColor(request.leave_type)}`}>
                      {request.leave_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>{formatDate(request.start_date)}</div>
                    {request.start_date !== request.end_date && (
                      <div className="text-gray-500">to {formatDate(request.end_date)}</div>
                    )}
                    {request.is_partial_day && (
                      <div className="text-xs text-blue-600">
                        {formatTime(request.start_time)} - {formatTime(request.end_time)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {request.total_days} day{request.total_days !== 1 ? 's' : ''}
                    {request.is_partial_day && (
                      <div className="text-xs text-gray-500">Partial day</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(request.status)}`}>
                      {request.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {request.submitted_at ? formatDate(request.submitted_at) : 'Not submitted'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedRequest(request);
                        setShowDetailModal(true);
                      }}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {leaveRequests.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg">No leave requests found</div>
            <p className="text-gray-400 mt-2">
              {selectedStatus === 'all' 
                ? 'Create a new leave request to get started' 
                : `No requests with status "${selectedStatus}"`
              }
            </p>
          </div>
        )}
      </div>

      {/* Create Request Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">Create Leave Request</h2>
              
              <form onSubmit={handleCreateRequest} className="space-y-4">
                {/* Employee Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee *
                  </label>
                  <select
                    value={formData.employee_number}
                    onChange={(e) => setFormData({...formData, employee_number: e.target.value})}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                  >
                    <option value="">Select Employee</option>
                    {employees.map((emp) => (
                      <option key={emp.employee_number} value={emp.employee_number}>
                        {emp.employee_name} (#{emp.employee_number})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Leave Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Leave Type *
                  </label>
                  <select
                    value={formData.leave_type}
                    onChange={(e) => setFormData({...formData, leave_type: e.target.value})}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                  >
                    <option value="Vacation">Vacation</option>
                    <option value="Sick Leave">Sick Leave</option>
                    <option value="Personal Unpaid">Personal Unpaid</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Partial Day Toggle */}
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_partial_day}
                      onChange={(e) => setFormData({
                        ...formData, 
                        is_partial_day: e.target.checked,
                        start_time: e.target.checked ? formData.start_time : '',
                        end_time: e.target.checked ? formData.end_time : '',
                        end_date: e.target.checked ? formData.start_date : formData.end_date
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Partial Day Request
                    </span>
                  </label>
                </div>

                {/* Date Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({
                        ...formData, 
                        start_date: e.target.value,
                        end_date: formData.is_partial_day ? e.target.value : formData.end_date
                      })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      required
                    />
                  </div>
                  
                  {!formData.is_partial_day && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date *
                      </label>
                      <input
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                        min={formData.start_date}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                        required
                      />
                    </div>
                  )}
                </div>

                {/* Time Fields for Partial Day */}
                {formData.is_partial_day && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Time *
                      </label>
                      <input
                        type="time"
                        value={formData.start_time}
                        onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Time *
                      </label>
                      <input
                        type="time"
                        value={formData.end_time}
                        onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                        min={formData.start_time}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                        required
                      />
                    </div>
                  </div>
                )}

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason
                  </label>
                  <input
                    type="text"
                    value={formData.reason}
                    onChange={(e) => setFormData({...formData, reason: e.target.value})}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="Optional reason for leave"
                  />
                </div>

                {/* Comments */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Admin Comments
                  </label>
                  <textarea
                    value={formData.comments}
                    onChange={(e) => setFormData({...formData, comments: e.target.value})}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    rows="3"
                    placeholder="Optional admin notes"
                  />
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create Request'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Request Detail Modal */}
      {showDetailModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-bold">Leave Request Details</h2>
                  <p className="text-gray-600">{selectedRequest.leave_request_no}</p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium text-gray-900">Employee Information</h3>
                    <p className="text-sm text-gray-600">{selectedRequest.employee_name}</p>
                    <p className="text-sm text-gray-500">#{selectedRequest.employee_number}</p>
                  </div>

                  <div>
                    <h3 className="font-medium text-gray-900">Leave Details</h3>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Type:</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getLeaveTypeColor(selectedRequest.leave_type)}`}>
                          {selectedRequest.leave_type}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Start Date:</span>
                        <span>{formatDate(selectedRequest.start_date)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">End Date:</span>
                        <span>{formatDate(selectedRequest.end_date)}</span>
                      </div>
                      {selectedRequest.is_partial_day && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Start Time:</span>
                            <span>{formatTime(selectedRequest.start_time)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">End Time:</span>
                            <span>{formatTime(selectedRequest.end_time)}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Days:</span>
                        <span>{selectedRequest.total_days}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Working Days:</span>
                        <span>{selectedRequest.working_days_affected}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium text-gray-900">Status Information</h3>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Current Status:</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(selectedRequest.status)}`}>
                          {selectedRequest.status}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Submitted:</span>
                        <span>{selectedRequest.submitted_at ? formatDate(selectedRequest.submitted_at) : 'Not submitted'}</span>
                      </div>
                      {selectedRequest.reviewed_at && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Reviewed:</span>
                          <span>{formatDate(selectedRequest.reviewed_at)}</span>
                        </div>
                      )}
                      {selectedRequest.reviewed_by && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Reviewed by:</span>
                          <span>#{selectedRequest.reviewed_by}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedRequest.reason && (
                    <div>
                      <h3 className="font-medium text-gray-900">Reason</h3>
                      <p className="text-sm text-gray-600">{selectedRequest.reason}</p>
                    </div>
                  )}

                  {selectedRequest.admin_comments && (
                    <div>
                      <h3 className="font-medium text-gray-900">Admin Comments</h3>
                      <p className="text-sm text-gray-600">{selectedRequest.admin_comments}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              {selectedRequest.status === 'Submitted' && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="font-medium text-gray-900 mb-3">Admin Actions</h3>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleStatusUpdate(selectedRequest.id, 'Approved')}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                      disabled={loading}
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        const comments = prompt('Enter rejection reason:');
                        if (comments) {
                          handleStatusUpdate(selectedRequest.id, 'Rejected', comments);
                        }
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      disabled={loading}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              )}

              {selectedRequest.status === 'Approved' && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="font-medium text-gray-900 mb-3">Payment Status</h3>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleStatusUpdate(selectedRequest.id, 'Paid')}
                      className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                      disabled={loading}
                    >
                      Mark as Paid
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(selectedRequest.id, 'Unpaid')}
                      className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
                      disabled={loading}
                    >
                      Mark as Unpaid
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveRequestManager;